import { useState } from "react";
import { Bell, Check, Trash2, Wifi, WifiOff, X } from "lucide-react";
import { useNotifications } from "../context/NotificationContext";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";

export default function NotificationBell({ darkMode }) {
  const { 
    notifications, 
    unreadCount, 
    connected,
    usePolling,
    clearNotifications, 
    markAsRead 
  } = useNotifications();
  const [open, setOpen] = useState(false);

  const handleOpen = (isOpen) => {
    setOpen(isOpen);
    if (isOpen) {
      markAsRead();
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case "error": return "bg-red-500";
      case "warning": return "bg-amber-500";
      case "success": return "bg-emerald-500";
      default: return "bg-blue-500";
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  // Connection status text
  const getStatusText = () => {
    if (connected) return "Real-time notifications active";
    if (usePolling) return "Polling mode (checking every 30s)";
    return "Reconnecting...";
  };

  return (
    <DropdownMenu open={open} onOpenChange={handleOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          data-testid="notification-bell"
        >
          <Bell className={`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
          
          {/* Connection indicator */}
          <span 
            className={`absolute top-1 right-1 w-2 h-2 rounded-full ${
              connected ? 'bg-emerald-500' : usePolling ? 'bg-amber-500' : 'bg-red-500'
            }`}
            title={connected ? "Connected" : usePolling ? "Polling mode" : "Disconnected"}
          />
          
          {/* Unread badge */}
          {unreadCount > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1.5 bg-[#DC143C] text-white border-0 text-xs"
              data-testid="notification-badge"
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent 
        align="end" 
        className={`w-80 p-0 ${darkMode ? 'bg-[#161b22] border-[#30363d]' : 'bg-white'}`}
        data-testid="notification-dropdown"
      >
        {/* Header */}
        <div className={`px-4 py-3 border-b ${darkMode ? 'border-[#30363d]' : 'border-gray-100'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className={`font-semibold text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Notifications
              </h3>
              {connected ? (
                <Wifi className="w-3 h-3 text-emerald-500" />
              ) : (
                <WifiOff className="w-3 h-3 text-red-500" />
              )}
            </div>
            {notifications.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  clearNotifications();
                }}
                className="h-7 text-xs"
              >
                <Trash2 className="w-3 h-3 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </div>
        
        {/* Notifications List */}
        <div className="max-h-[400px] overflow-y-auto">
          {notifications.length > 0 ? (
            notifications.slice(0, 10).map((notification, idx) => (
              <div
                key={notification.id || `${notification.timestamp || notification.title}-${idx}`}
                className={`px-4 py-3 border-b last:border-0 ${
                  darkMode 
                    ? 'border-[#21262d] hover:bg-[#21262d]' 
                    : 'border-gray-50 hover:bg-gray-50'
                } transition-colors`}
              >
                <div className="flex items-start gap-3">
                  {/* Severity indicator */}
                  <div className={`w-2 h-2 rounded-full mt-2 ${getSeverityColor(notification.severity)}`} />
                  
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {notification.title}
                    </p>
                    <p className={`text-xs mt-0.5 line-clamp-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {notification.message}
                    </p>
                    <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {formatTime(notification.timestamp)}
                    </p>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className={`p-8 text-center ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No notifications yet</p>
              <p className="text-xs mt-1">
                {connected 
                  ? "You'll see alerts here when competitors post viral content"
                  : "Connecting to notification server..."}
              </p>
            </div>
          )}
        </div>
        
        {/* Footer */}
        {notifications.length > 0 && (
          <>
            <DropdownMenuSeparator className={darkMode ? 'bg-[#30363d]' : ''} />
            <div className={`px-4 py-2 text-center ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              <p className="text-xs">
                {getStatusText()}
              </p>
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
